import React from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { useTheme, lighten } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { Link } from 'react-router';
import { Button } from '../Buttons/Button';
import { IconButton } from '../Buttons/IconButton';
import { Typography } from '../Typography';
export interface BreadcrumbCopyProps {
  copyRef: string;
  onRef: (ref: string) => void;
  tooltip: string;
  ariaLabel: string;
}
export interface BreadcrumbProps {
  // render wins if provided. if href exists render a Link. else if onClick, render a button, else plain typography
  label: string;
  render?: () => React.ReactNode;
  href?: string;
  onClick?: () => void;
  isLast?: boolean;
  highlightLastItem?: boolean;
  loading?: boolean;
  /** Used by parent Breadcrumbs to identify items with a copyable reference, which it then passes via `copy` */
  copyRef?: string;
  copy?: BreadcrumbCopyProps;
}
export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  label,
  render,
  href,
  onClick,
  isLast,
  highlightLastItem,
  loading,
  copy,
}) => {
  const { palette } = useTheme();
  const getItem = () => {
    const styles = {
      color: isLast && highlightLastItem ? palette.primary.main : '#717680',
      fontSize: '14px',
      fontWeight: 600,
      fontStyle: 'normal',
      textDecoration: 'none',
    };
    if (loading) return <Skeleton variant="text" width={100} />;
    if (render) return render();
    if (href) {
      return (
        <Link to={{ pathname: href }} style={styles}>
          {label}
        </Link>
      );
    }
    if (onClick) return <Button onClick={onClick}>{label}</Button>;
    return (
      <Typography sx={styles} variant="body2">
        {label}
      </Typography>
    );
  };
  return (
    <Box
      data-testid="breadcrumb-container"
      sx={{
        backgroundColor: isLast && highlightLastItem ? `${lighten(palette.primary.main, 0.9)}` : 'transparent',
        borderRadius: isLast ? '6px' : '0',
        padding: isLast ? '4px 8px' : '0',
      }}
    >
      {copy ? (
        <Box display="flex" alignItems="center" gap={0.5}>
          {getItem()}
          <Tooltip title={copy.tooltip}>
            <IconButton
              size="small"
              aria-label={copy.ariaLabel}
              sx={{ opacity: 0.8 }}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                e.stopPropagation();
                copy.onRef(copy.copyRef);
              }}
            >
              <ContentCopyIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        getItem()
      )}
    </Box>
  );
};
